import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types';
import api from '../../services/api';
import { fullName } from '../../utils/name';
import './UsersList.css';

interface UsersListProps {
  users: User[];
  loading: boolean;
  onUserDeleted?: () => void;
  /** When set (e.g. Admin User view), hide Edit/Delete for this user (cannot edit/delete self) */
  currentUserId?: number;
}

const UsersList: React.FC<UsersListProps> = ({ users, loading, onUserDeleted, currentUserId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }
    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      return (
        (user.first_name || '').toLowerCase().includes(term) ||
        (user.last_name || '').toLowerCase().includes(term) ||
        fullName(user).toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      );
    });
  }, [users, searchTerm]);

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (users.length === 0) {
    return <div className="no-data">No users found</div>;
  }

  const handleEditClick = (e: React.MouseEvent, userId: number) => {
    e.stopPropagation();
    navigate(`/dashboard/users/${userId}`);
  };

  const openDeleteModal = (e: React.MouseEvent, user: User) => {
    e.stopPropagation();
    setUserToDelete(user);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/users/${userToDelete.id}`);
      setUserToDelete(null);
      onUserDeleted?.();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="users-list">
      <div className="search-filter">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <table className="users-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Companies</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length === 0 ? (
            <tr>
              <td colSpan={7} className="no-results">
                No users found matching your search.
              </td>
            </tr>
          ) : (
            filteredUsers.map((user) => {
              const isCurrentUser = currentUserId != null && user.id === currentUserId;
              return (
            <tr 
              key={user.id}
              onClick={() => !isCurrentUser && navigate(`/dashboard/users/${user.id}`)}
              style={{ cursor: isCurrentUser ? 'default' : 'pointer' }}
            >
              <td>{user.id}</td>
              <td className="user-name">{user.first_name}</td>
              <td className="user-name">{user.last_name}</td>
              <td>{user.email}</td>
              <td>
                <span className={`role-badge ${user.role}`}>
                  {user.role === 'user_admin' ? 'Admin User' : user.role}
                </span>
              </td>
              <td>
                {user.clients && user.clients.length > 0 ? (
                  <div className="client-tags">
                    {user.clients.map((client) => (
                      <span key={client.id} className="client-tag">
                        {client.client_name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="no-clients">—</span>
                )}
              </td>
              <td className="user-actions-cell" onClick={(e) => e.stopPropagation()}>
                {!isCurrentUser && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => handleEditClick(e, user.id)}
                      className="user-action-btn user-edit-btn"
                      title="Edit user"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => openDeleteModal(e, user)}
                      className="user-action-btn user-delete-btn"
                      title="Delete user"
                    >
                      🗑️ Delete
                    </button>
                  </>
                )}
                {isCurrentUser && <span className="text-muted">(you)</span>}
              </td>
            </tr>
              );
            })
          )}
        </tbody>
      </table>

      {userToDelete && (
        <div className="modal-overlay" onClick={() => !deleting && setUserToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p>Are you sure you want to delete <strong>{fullName(userToDelete)}</strong>?</p>
            <p className="warning-text">This action cannot be undone. The user will be soft-deleted.</p>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setUserToDelete(null)}
                className="btn btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
